// src/services/voucherService.js
import { db } from "../firebase";
import { runTransaction, doc, collection, serverTimestamp } from "firebase/firestore";
import {
  getFinancialYearCodeForDate,
  getReferenceDateFromInvoicePayload,
} from "../utils/financialYear";

const prefixMap = {
  inward: "PINV",
  outward: "SINV",
  creditnote: "CRN",
  debitnote: "DBN",
  materialrequest: "MRQ",
};

// ✅ COUNTER ONLY (use for preview/standalone needs; NOT for final save flow)
/** @param {{ referenceDate?: Date }} [options] FY segment follows invoice date when provided, else today */
export async function generateVoucherNo(type, options) {
  if (!type || typeof type !== "string") {
    throw new Error("Invoice type is required (inward, outward, creditnote, debitnote, materialrequest)");
  }
  const t = type.toLowerCase();
  const prefix = prefixMap[t];
  if (!prefix) {
    throw new Error("Invalid invoice type (inward, outward, creditnote, debitnote, materialrequest)");
  }

  const ref =
    options?.referenceDate instanceof Date && !Number.isNaN(options.referenceDate.getTime())
      ? options.referenceDate
      : new Date();
  const fyCode = getFinancialYearCodeForDate(ref);
  const counterRef = doc(db, "counters", `${prefix}-${fyCode}`);

  // This WILL increment even if you don't save the invoice later → use carefully.
  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const lastNo = snap.exists() ? (snap.data().lastNo || 0) : 0;
    const newNo = lastNo + 1;

    tx.set(counterRef, { lastNo: newNo }, { merge: true });

    const voucherNo = `${prefix}-${fyCode}-${String(newNo).padStart(4, "0")}`;
    return { voucherNo, voucherNoNum: newNo, fyCode, prefix };
  });
}

// ✅ ATOMIC SAVE (no gaps). Use this for the actual invoice save flow.
/** @param {{ voucherNoOverride?: string }} [options] Keep Find/import voucher number (no new SINV counter). */
export async function createInvoice(data, type, options = {}) {
  if (!type || typeof type !== "string") {
    throw new Error("Invoice type is required (inward, outward, creditnote, debitnote, materialrequest)");
  }
  const t = type.toLowerCase();
  const prefix = prefixMap[t];
  if (!prefix) {
    throw new Error("Invalid invoice type (inward, outward, creditnote, debitnote, materialrequest)");
  }

  const voucherNoOverride = String(options.voucherNoOverride || "").trim();
  const invoicesRef = collection(db, "invoices");

  if (voucherNoOverride) {
    return await runTransaction(db, async (tx) => {
      const newInvoiceRef = doc(invoicesRef);
      tx.set(newInvoiceRef, {
        ...data,
        type: t,
        voucherNo: voucherNoOverride,
        voucherNoNum: null,
        fyCode: data.fyCode || null,
        importSource: data.importSource || null,
        importPeriodKey: data.importPeriodKey || null,
        created: serverTimestamp(),
      });
      return { id: newInvoiceRef.id, voucherNo: voucherNoOverride, voucherNoNum: null };
    });
  }

  const fyCode = getFinancialYearCodeForDate(getReferenceDateFromInvoicePayload(data));
  const counterRef = doc(db, "counters", `${prefix}-${fyCode}`);

  return await runTransaction(db, async (tx) => {
    // 1) read counter
    const counterSnap = await tx.get(counterRef);
    const lastNo = counterSnap.exists() ? (counterSnap.data().lastNo || 0) : 0;
    const newNo = lastNo + 1;

    // 2) build voucher no
    const voucherNo = `${prefix}-${fyCode}-${String(newNo).padStart(4, "0")}`;

    // 3) write invoice
    const newInvoiceRef = doc(invoicesRef); // auto id
    tx.set(newInvoiceRef, {
      ...data,
      type: t,
      voucherNo,
      voucherNoNum: newNo,
      fyCode,
      created: serverTimestamp(),
    });

    // 4) update counter
    tx.set(counterRef, { lastNo: newNo }, { merge: true });

    return { id: newInvoiceRef.id, voucherNo, voucherNoNum: newNo };
  });
}
